import React, { memo } from 'react';
import PropTypes from 'prop-types';
import StackedBar from './StackedBar';
import { pct, ratioTone, fmtCount, RATIO_TONES, SEGMENT_COLORS, EMPTY } from './objectiveFormat';

const METRICS = [
  {
    key:      'schedule',
    label:    'Meet CSD schedule',
    ratioKey: 'meet_schedule_ratio',
    counts:   ['meet_schedule', 'delayed_schedule', 'pending_schedule'],
    // Demand-vs-capacity: no company target, so the number stays neutral and
    // the bar carries the magnitude. See ratioTone() in objectiveFormat.
    graded:   false,
  },
  {
    key:      'committed',
    label:    'Meet own committed schedule',
    ratioKey: 'meet_committed_ratio',
    counts:   ['meet_committed', 'delayed_committed', 'pending_committed'],
    graded:   true,
  },
];

const SEGMENT_LABELS = ['Met', 'Delayed', 'Pending'];
const SEGMENT_KEYS   = ['met', 'delayed', 'pending'];

const Metric = ({ stats, metric }) => {
  const ratio = stats?.[metric.ratioKey];
  const tone = metric.graded ? ratioTone(ratio) : RATIO_TONES.neutral;
  const segments = metric.counts.map((countKey, index) => ({
    key:   SEGMENT_KEYS[index],
    label: SEGMENT_LABELS[index],
    value: stats?.[countKey] ?? 0,
    color: SEGMENT_COLORS[SEGMENT_KEYS[index]],
  }));

  return (
    <div>
      <div className="tw-mb-1.5 tw-flex tw-items-baseline tw-justify-between tw-gap-2">
        <span className="tw-text-[11px] tw-font-medium tw-text-slate-500">{metric.label}</span>
        <span className="tw-text-lg tw-font-bold tw-tabular-nums tw-leading-none" style={{ color: tone }}>
          {pct(ratio)}
        </span>
      </div>
      <StackedBar segments={segments} total={stats?.total ?? 0} />
    </div>
  );
};
Metric.propTypes = { stats: PropTypes.object, metric: PropTypes.object.isRequired };

/**
 * One drawing stage (APD / SHOP / ERD) for the selected scope: how many jobs
 * are in scope, then the two headline rates as bars rather than a wall of
 * percentages.
 */
const StageCard = ({ title, subtitle, stats }) => {
  const empty = !stats || stats.total === 0;

  return (
    <div className="tw-flex tw-min-w-[260px] tw-flex-1 tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4 tw-shadow-sm">
      <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-2">
        <div>
          <h4 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-slate-800">{title}</h4>
          <p className="tw-mb-0 tw-text-[11px] tw-text-slate-400">{subtitle}</p>
        </div>
        <span className="tw-text-2xl tw-font-bold tw-tabular-nums tw-leading-none tw-text-slate-800">
          {empty ? EMPTY : fmtCount(stats.total)}
        </span>
      </div>

      {empty ? (
        <p className="tw-mb-0 tw-py-4 tw-text-center tw-text-[11px] tw-italic tw-text-slate-300">
          No job reached this stage in the current range
        </p>
      ) : (
        METRICS.map((metric) => <Metric key={metric.key} stats={stats} metric={metric} />)
      )}
    </div>
  );
};

StageCard.propTypes = {
  title:    PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  stats:    PropTypes.object,
};

export default memo(StageCard);
